import { Component, OnDestroy, OnInit } from '@angular/core';
import { OrchestrationService } from '../services/orchestration-service/orchestration.service';
import { select } from '@angular-redux/store';
import { Observable } from 'rxjs';
import { ApiResponseState } from '../../store/api-response/api-response-state.model';
import { ApiResponseActions } from '../../store/api-response/api-response-actions';
import { MatDialog } from '@angular/material';
import { TssDialogBoxComponent } from '../../common/components/tss-dialog-box/tss-dialog-box.component';
import { transformDateTime } from '../../common/helpers/utils';
import { NgFor, NgClass } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';

@Component({
    selector: 'app-tss',
    templateUrl: './tss.component.html',
    styleUrls: ['./tss.component.scss'],
    imports: [NgFor, NgClass, TranslatePipe]
})
export class TssComponent implements OnInit, OnDestroy {
  /**
   * @Property to observe the redux customer store
   **/
  @select('api') readonly apiObservable: Observable<ApiResponseState>;
  /**
   * @property to store the api subscription
   */
  private apiSubscription: any;
  /**
   * @property to store the tss polling interval so it can be cleared on destroy
   */
  private tssPollingInterval: any;
  /**
   * @property used to store the telematics services list
   */
  public servicesList: Array<any>;
  /**
   * @property used to store the HDWeatherandTraffic service records list
   */
  public HDWeatherandTraffic: object;
  /**
   * @property used to store the WirelessNetwork service records list
   */
  public WirelessNetwork: object;
  /**
   * @property used to store the last updated date
   */
  public lastUpdatedDate: any;
  /**
   * @property used to store the records of the services
   */
  public servicesListRecords: object;
  /**
   * @property used to store the selected service name
   */
  public serviceSelectedName: any;
  /**
   * @property used to store the list of available services to show on the service status card
   */
  public listOfServices = [
    'Remote Services',
    'Scout GPS Link',
    'Entune/Enform App Suite',
    'Entune 3.0/Enform 2.0 App Suite',
    'Toyota / Lexus app',
    'Safety Connect',
    'Destination Assist',
    'Service Connect',
    'Lexus Drivers',
    'Toyota Owners',
    'Enrollment  / Registration (Customer)',
    'Enrollment / Registration (Dealer)',
    'Dealer Daily',
    'CTP Admin Portal',
    'HDWeather & Traffic',
    'Wireless Network',
    'Monitoring Test',
    'ADR',
    'Amazon Music',
    'Connect Audio',
    'Connected Radio',
    'Digital Key',
    'HuCom',
    'IPApps',
    'Navigation',
    'Remote Parking',
    'User Profile',
    'Va & Search',
    'Wifi',
    'Rent A Toyota',
    'Service Shop',
    'EV',
    'Service History',
    'Owners Manuals',
    'TFS/LFS',
  ];

  constructor(private readonly orch: OrchestrationService, public dialog: MatDialog) {}

  /**
   * @function ngOnInit sets initial configuration
   */
  ngOnInit() {
    this.apiSubscription = this.apiObservable.subscribe((api) => {
      if (api.tssResponse) {
        if (
          api.apiAction === ApiResponseActions.SET_TSS &&
          api.tssResponse['type'] === 'success'
        ) {
          const response = api.tssResponse['payload']['servicesList'];
          this.HDWeatherandTraffic = {
            cannedMessages: response['hdOutageDetails'],
            name: 'HDWeather & Traffic',
            status:
              response['hdOutageDetails'].length < 1 ? 'Normal' : 'Limited',
            applicationName: 'HDWeatherandTraffic',
          };

          this.WirelessNetwork = {
            cannedMessages: response['wirelessOutageDetails'],
            name: 'Wireless Network',
            status:
              response['wirelessOutageDetails'].length < 1
                ? 'Normal'
                : 'Limited',
            applicationName: 'WirelessNetwork',
          };
          this.servicesList = [].concat(
            response['apps'],
            response['services'],
            response['websites'],
            this.HDWeatherandTraffic,
            this.WirelessNetwork
          );
          for (let count = 0; count < this.servicesList.length; count++) {
            if (this.servicesList[count].name.includes('21MM')) {
              this.servicesList[count].name =
                this.servicesList[count].name.slice(5);
            }
            const service = this.servicesList[count];
            if (service.name === 'Amazon Music' || service.name.startsWith('Amazon /')) {
              this.servicesList[count] = {
                ...service,
                name: 'Amazon Music',
                applicationName: '21MMAmazonMusic'
              };
            }
            if (
              this.listOfServices.indexOf(this.servicesList[count].name) < 0
            ) {
              this.servicesList.splice(count, 1);
              count = count - 1;
            }
          }
          this.getServicesListRecords(this.servicesList);
          const date = new Date();
          this.lastUpdatedDate = transformDateTime(date);
        }
      }
    });
    this.getTss();
    this.tssPollingInterval = setInterval(() => {
      this.getTss();
    }, 900000);
  }
  /**
   * ondestroy method
   */
  ngOnDestroy() {
    if (this.apiSubscription) {
      this.apiSubscription.unsubscribe();
    }
    if (this.tssPollingInterval) {
      clearInterval(this.tssPollingInterval);
      this.tssPollingInterval = null;
    }
  }
  /**
   * @function getTss retrieves the status of telematics services
   */
  private getTss() {
    this.orch.getTss();
  }

  /**
   * @method showRecords displays the records list
   * @arg records records contains list of records
   */
  public showRecords(selectedIndex): void {
    this.serviceSelectedName = this.servicesList[selectedIndex].applicationName;
    this.dialog.open(TssDialogBoxComponent, {
      data: {
        type: this.servicesListRecords[this.serviceSelectedName],
      },
      width: '39em',
      maxHeight: '35em',
    });
  }

  /**
   * @method getServicesListRecords gets the list of all the limited services
   * @arg response  contains list of all the services
   */
  public getServicesListRecords(response) {
    this.servicesListRecords = {};
    response.forEach((element) => {
      const temp = {
        cannedMessages: element.cannedMessages,
        name: element.name,
        status: element.status,
        applicationName: element.applicationName,
        comment: element.comment,
      };
      this.servicesListRecords[element.applicationName] = temp;
    });
    this.servicesListRecords['HDWeatherandTraffic'] = this.HDWeatherandTraffic;
    this.servicesListRecords['WirelessNetwork'] = this.WirelessNetwork;
  }
}
